// The onboarding checklist, drawn (UI rebuild batch 5, 27 Oct 2026; onboarding-bot docs/onboarding/CHECKLIST-SPEC.md).
//
// SEVEN SECTIONS, ONE VOCABULARY. The worker writes every word — the titles, the one line, the detail — and they are the
// Telegram card's, so the operator reads the same words in both places. The screen keeps the order (the wallet first:
// "Wallet screening and matching against their claim is still most important", the operator, 27 Oct) and the words, and
// adds only the status in a word beside its colour: checked → good, look → warn, failed → bad, pending → info.
//
// NEVER MADE UP: a row with no checklist (an older worker, an approved client) falls back to the four older checks; a
// checklist with no sections is no checklist; a section the worker did not send is "Not known", never "Checked"; a
// status this screen does not know is "Not known" too. KYT and screening are information here, never a verdict.
//
// THE HEADLINE (the operator, 27 Oct: "Buyer and seller /MC /bank transfer etc also should be clearly visible on customer
// card in the dashboard"): Buyer / Seller from claim.side, the amount and coin, and the method from claim.method — the
// customer's stated choices, as labels only, with no rail logic — falling back to claim.type 'mc' → Manager's cheque.
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import type { PillTone } from '../../Pill';
import { amountWords } from '../../OnboardingParts';
import {
  CHECKS, VERDICT,
  type Assessment, type Check, type CheckKey, type Checklist, type ChecklistKey, type ChecklistSection, type ChecklistStatus, type ClaimMethod,
} from '../../../lib/onboarding';

/** The seven, in the model's order, with the short title a row's chip carries. */
export const SECTIONS: ReadonlyArray<{ key: ChecklistKey; short: string; title: string }> = [
  { key: 'wallet', short: 'Wallet', title: 'Wallet check' },
  { key: 'id', short: 'ID', title: 'ID verification' },
  { key: 'requirement', short: 'Requirement', title: 'Requirement' },
  { key: 'presence', short: 'Residency', title: 'In the UAE & residency' },
  { key: 'funds', short: 'Funds', title: 'Source of funds' },
  { key: 'mismatch', short: 'Claim', title: 'Claim vs reality' },
  { key: 'history', short: 'History', title: 'History' },
];

/** The status in a word, and its tone. */
export const STATUS: Record<ChecklistStatus, { word: string; tone: PillTone }> = {
  checked: { word: 'Checked', tone: 'good' },
  look: { word: 'Look', tone: 'warn' },
  failed: { word: 'Failed', tone: 'bad' },
  pending: { word: 'Pending', tone: 'info' },
};
/** The four older checks' verdicts, in the same tones. */
const VERDICT_TONE: Record<Check['status'], PillTone> = { passed: 'good', pending: 'info', flagged: 'bad' };

const TONE: Record<PillTone, string> = {
  neutral: 'bg-panel-2 text-tg-hint',
  good: 'bg-pos-bg text-pos',
  warn: 'bg-warn-bg text-warn',
  bad: 'bg-neg-bg text-neg',
  info: 'bg-info-bg text-info',
  gold: 'bg-brand-gold/10 text-brand-gold',
};
export const toneClass = (t: PillTone) => TONE[t];
const INK: Record<PillTone, string> = { neutral: 'text-tg-hint', good: 'text-pos', warn: 'text-warn', bad: 'text-neg', info: 'text-info', gold: 'text-brand-gold' };

/** One section as the screen draws it — the worker's, or "Not known" for one it did not send or a status not known here. */
/** The worker writes dates as 2031-05-05; the console says them its way, "5 May 2031" — never broken mid-date on the phone
 *  (the audit, 1 Nov 2026). Only a whole ISO day is rewritten. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function dayWords(text: string): string {
  return text.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (m, y: string, mo: string, d: string) => {
    const i = Number(mo) - 1;
    return i >= 0 && i < 12 && Number(d) >= 1 && Number(d) <= 31 ? `${Number(d)}\u00a0${MONTHS[i]}\u00a0${y}` : m;
  });
}

export interface Drawn { key: string; short: string; title: string; word: string; tone: PillTone; line: string | null; detail: string[]; ask: CheckKey | null }

/** Which "Ask the customer" form a section opens: the four older checks' own items (lib/onboarding.ts ITEMS). */
const ASK_OF: Partial<Record<ChecklistKey, CheckKey>> = { wallet: 'wallet', id: 'kyc', presence: 'residency', funds: 'funds' };

/** The checklist when there is one to draw: sent, with sections. Never an empty checklist. */
export const usable = (c: Checklist | null | undefined): c is Checklist => !!c && Array.isArray(c.sections) && c.sections.length > 0;

/** The seven in order, from the worker's checklist. */
export function drawChecklist(c: Checklist): Drawn[] {
  return SECTIONS.map((s) => {
    const x: ChecklistSection | undefined = c.sections.find((y) => y.key === s.key);
    const st = x && Object.hasOwn(STATUS, x.status) ? STATUS[x.status] : null;
    return {
      key: s.key, short: s.short, title: x?.title || s.title,
      word: st ? st.word : 'Not known', tone: st ? st.tone : 'neutral',
      line: x?.line ?? null, detail: Array.isArray(x?.detail) ? x.detail : [], ask: ASK_OF[s.key] ?? null,
    };
  });
}

/** The four older checks, drawn the same way (a row with no checklist). Null: none sent — nothing drawn. */
export function drawAssessment(a: Assessment | null | undefined): Drawn[] | null {
  if (!a) return null;
  return CHECKS.map((k) => {
    const c = a[k.key];
    return {
      key: k.key, short: k.short, title: k.name,
      word: c ? VERDICT[c.status] : 'Not known', tone: c ? VERDICT_TONE[c.status] : 'neutral',
      line: c?.reason ?? null, detail: [], ask: k.key,
    };
  });
}

/** What a row or record draws: the checklist, else the four checks, else nothing. */
export const drawnOf = (checklist: Checklist | null | undefined, a: Assessment | null | undefined): { kind: 'checklist' | 'checks'; rows: Drawn[] } | null => {
  if (usable(checklist)) return { kind: 'checklist', rows: drawChecklist(checklist) };
  const rows = drawAssessment(a);
  return rows ? { kind: 'checks', rows } : null;
};

// ── the headline ────────────────────────────────────────────────────────────────────────────────────────────────────

const METHOD_WORD: Record<ClaimMethod, string> = { manager_cheque: "Manager's cheque", bank_transfer: 'Bank transfer', cash: 'Cash', crypto: 'Crypto' };
const SIZE_WORD: Record<string, string> = { under_50k: 'under 50k AED', '50k_200k': '50–200k AED', '200k_1m': '200k–1M AED', over_1m: 'over 1M AED', unclear: 'size unclear' };

/** What the headline is read from: a row's claim or a record's answers. */
export interface ClaimLike {
  side: 'buy' | 'sell' | null;
  asset: string | null;
  sizeBand?: string | null;
  type: 'buy' | 'sell' | 'mc' | 'developer' | null;
  amountAed: { low: number; high: number; upTo: boolean } | null;
  method?: ClaimMethod | null;
}

/** How they pay or are paid, in words: claim.method, else a cheque by the kind of trade; a developer payout as before. */
export function methodWords(c: ClaimLike | null): string | null {
  if (c?.method && Object.hasOwn(METHOD_WORD, c.method)) return METHOD_WORD[c.method];
  if (c?.type === 'mc') return "Manager's cheque";
  if (c?.type === 'developer') return 'Developer payment';
  return null;
}

/** The headline's facts apart, for a register that gives each its own seat (his "one fact per place", 1 Nov 2026): the side
 *  ("Seller"), the size ("150,000 AED", or their own words), the coin, how it is paid. */
export function tradeParts(c: ClaimLike | null, size?: string | null): { side: string | null; amount: string | null; asset: string | null; method: string | null } {
  if (!c) return { side: null, amount: size ?? null, asset: null, method: null };
  return {
    side: c.side === 'sell' ? 'Seller' : c.side === 'buy' ? 'Buyer' : null,
    amount: amountWords(c.amountAed) ?? size ?? (c.sizeBand ? SIZE_WORD[c.sizeBand] ?? null : null),
    asset: c.asset ?? null,
    method: methodWords(c),
  };
}

/** "Seller · 150,000 AED USDT · Manager's cheque" — or null with nothing said. `size`: their own words when no amount. */
export function headline(c: ClaimLike | null, size?: string | null): string | null {
  if (!c) return null;
  const t = tradeParts(c, size);
  const what = [t.amount, t.asset].filter(Boolean).join(' ') || null;
  const parts = [t.side, what, t.method].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

// ── a row's line ───────────────────────────────────────────────────────────────────────────────────────────────

/** Worst first: what the rules refuse, what needs a look, what is not there yet, what is not known — then the rest. */
const RANK: Record<string, number> = { Failed: 0, Flagged: 0, Look: 1, Pending: 2, 'Not known': 3 };

/** A row's checks in ONE line, in words, worst first, naming only what is not checked — "Funds failed · Claim look ·
 *  ID pending · 4 checked" — each word in its tone (Coordinator, round 2, under RULING-LIGHT). The seven with their
 *  lines live in the record. A section's line is its tooltip. */
export function ChecksLine({ drawn, compact = false }: { drawn: Drawn[]; compact?: boolean }) {
  // `compact` (a row): only what failed is named — what he decides on; what needs a look, the pending and the unknown are
  // counted (Astra, 1 Nov 2026: the rows read as reports). The record names them all.
  const all = drawn.filter((d) => d.word in RANK).sort((a, b) => (RANK[a.word] ?? 9) - (RANK[b.word] ?? 9));
  const named = compact ? all.filter((d) => (RANK[d.word] ?? 9) === 0) : all;
  const looks = compact ? all.filter((d) => RANK[d.word] === 1).length : 0;
  const waiting = compact ? all.length - named.length - looks : 0;
  const fine = drawn.filter((d) => !(d.word in RANK));
  const fineWord = fine[0]?.word.toLowerCase() ?? 'checked';
  return (
    <p className="break-words text-meta" data-checks="">
      {named.map((d, i) => (
        <span key={d.key} className={INK[d.tone]} title={d.line ? `${d.title} — ${d.line}` : d.title} data-section={d.key} data-status={d.word}>
          {i > 0 && <span className="text-tg-hint">{' · '}</span>}{d.short} {d.word.toLowerCase()}
        </span>
      ))}
      {looks > 0 && <span className="text-warn" data-status="looks">{named.length ? ' · ' : ''}{looks} to look at</span>}
      {waiting > 0 && <span className="text-tg-hint" data-status="waiting">{named.length || looks ? ' · ' : ''}{waiting} pending</span>}
      {fine.length > 0 && (
        <span className={named.length || looks || waiting ? 'text-tg-hint' : INK.good} data-status="fine">{named.length || looks || waiting ? ' · ' : ''}{fine.length} {fineWord}</span>
      )}
    </p>
  );
}

// ── the record's rows ──────────────────────────────────────────────────────────────────────────────────────────────

/** The record's sections: one row each — the status word in its colour, the title, the line in view — and, on a tap,
 *  the detail one fact a line, then what goes with it (`extra`: what was asked of the customer, and "Ask the customer"). */
export function SectionRows({ drawn, label, extra }: { drawn: Drawn[]; label: string; extra?: (d: Drawn) => ReactNode }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <ul className="divide-y divide-stroke-soft rounded-[10px] border border-stroke bg-panel" aria-label={label}>
      {drawn.map((d) => {
        const more = extra?.(d);
        // TERSE (his standing rule, ruled for the record 1 Nov 2026): a closed check is its verdict and title; the worker's
        // line of why is behind the tap — except a check that failed or is flagged, whose one short line is the warning he
        // acts on.
        const blocking = RANK[d.word] === 0;
        const lineInside = !!d.line && !blocking;
        const opens = d.detail.length > 0 || !!more || lineInside;
        const isOpen = open === d.key;
        const head = (
          <>
            <span className={`mt-0.5 inline-flex w-[72px] shrink-0 justify-center whitespace-nowrap rounded-full px-2 py-0.5 text-meta font-medium ${toneClass(d.tone)}`}>{d.word}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-body font-medium">{d.title}</span>
              {d.line && blocking && <span className="mt-0.5 block break-words text-body text-tg-hint">{dayWords(d.line)}</span>}
            </span>
            {opens && <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-tg-hint transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden />}
          </>
        );
        return (
          <li key={d.key} data-section={d.key} data-status={d.word}>
            {opens ? (
              <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : d.key)}
                className="flex min-h-[44px] w-full items-start gap-3 px-4 py-3 text-left hover:bg-panel-2 lg:min-h-[40px]">
                {head}
              </button>
            ) : <div className="flex items-start gap-3 px-4 py-3">{head}</div>}
            {isOpen && (
              <div className="space-y-3 px-4 pb-4 pl-[100px] max-sm:pl-4">
                {lineInside && <p className="break-words text-body text-tg-hint">{dayWords(d.line!)}</p>}
                {d.detail.length > 0 && (
                  <ul className="space-y-1">
                    {d.detail.map((x, i) => <li key={i} className="break-words text-body leading-snug">{dayWords(x)}</li>)}
                  </ul>
                )}
                {more}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
